import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, } from "react";
import { createUseStyles } from "react-jss";
import { SearchSize } from ".";
import { useTranslation } from "./hooks/useTranslation";
import { THEME } from "./theming/theming";
import { classNames } from "./utils/classNames";
import { keyPress } from "./utils/keyPress";
import { useDebounce } from "./utils/useDebounce";
export const Search = forwardRef(({ initialValue = "", onChange, label, debounceTime = 500, uniqueId = 0, componentSize, ariaDescription, iconPosition = "right", }, ref) => {
    const classes = useSearchStyles(componentSize, iconPosition);
    const t = useTranslation();
    const [value, setValue] = useState(initialValue);
    const debouncedValue = useDebounce(value, debounceTime);
    const useFocus = () => {
        const htmlElRef = useRef(null);
        const setFocus = () => {
            htmlElRef.current && htmlElRef.current.focus();
        };
        return [htmlElRef, setFocus];
    };
    const [inputRef, setInputFocus] = useFocus();
    useEffect(() => {
        onChange(debouncedValue);
    }, [onChange, debouncedValue]);
    let keysPressed = {};
    const keyFunctions = useCallback((event) => {
        keysPressed[event.keyCode] = true;
        if (keysPressed[keyPress.CONTROL] &&
            event.keyCode === keyPress.BACKSLASH) {
            setTimeout(() => setInputFocus(), 1);
        }
        if (value && event.keyCode === keyPress.ESCAPE) {
            onChange("");
            onClear();
        }
    }, [keysPressed, value, setInputFocus, onChange]);
    useEffect(() => {
        document.addEventListener("keydown", keyFunctions, false);
        document.addEventListener("keyup", (event) => {
            delete keysPressed[event.keyCode];
        });
        return () => {
            document.removeEventListener("keydown", keyFunctions, false);
        };
    }, [keyFunctions, keysPressed]);
    const onClear = () => setValue("");
    useImperativeHandle(ref, () => ({
        resetSearch() {
            setValue("");
        },
    }));
    return (React.createElement("div", { className: `${classes.root} search`, role: "search" },
        React.createElement("label", { className: classes.label, htmlFor: `searchBar${uniqueId}` },
            "Search ",
            label,
            ariaDescription && `, ${ariaDescription}`),
        React.createElement("input", { type: "search", placeholder: `${t("Search")} ${label}...`, onChange: (ev) => {
                setValue(ev.target.value);
            }, value: value, ref: inputRef, id: `searchBar${uniqueId}`, className: classes.input, role: "searchbox" }),
        value && (React.createElement("span", { role: "button", className: classNames("icon-remove", classes.remove, "glyphicon-pro", "glyphicon-pro-times"), "aria-label": t("Clear Search"), tabIndex: 0, onClick: onClear, onKeyPress: onClear })),
        React.createElement("span", { className: classNames(classes.icon, "glyphicon-search", "icon-search"), "aria-hidden": "true" })));
});
const useSearchStyles = (componentSize, iconPosition) => {
    const { gray, primary } = THEME.palette;
    let icon = {
        top: 7,
        right: 9,
        position: "absolute",
        color: primary.base.color,
        fontFamily: "Glyphicons Halflings",
        fontStyle: "normal",
        fontWeight: 400,
    };
    let remove = { top: 6.5, right: 32, position: "absolute", fontSize: 18 };
    let input = {
        fontSize: 14,
        padding: {
            top: 6,
            right: 10,
            bottom: 7,
            left: 16,
        },
        width: "100%",
        border: 0,
        background: "transparent",
        color: gray.charcoal.color,
        "&:placeholder": {
            color: gray.silver.color,
        },
        "&:-ms-input-placeholder": {
            color: gray.silver.color,
        },
        height: 35,
    };
    if (componentSize === SearchSize.LG) {
        icon = {
            ...icon,
            top: 10,
        };
        remove = {
            ...remove,
            top: 10,
        };
        input = {
            ...input,
            height: 40,
            padding: {
                ...input.padding,
                top: 5,
            },
        };
    }
    if (componentSize === SearchSize.LG && iconPosition === "left") {
        icon = {
            ...icon,
            left: 12,
        };
        remove = {
            ...remove,
            right: 10,
        };
    }
    if (componentSize === SearchSize.SM) {
        icon = {
            ...icon,
            top: 5,
        };
        remove = {
            ...remove,
            right: 28,
            top: 5,
        };
        input = {
            ...input,
            height: 30,
            padding: {
                top: 6,
                left: 12,
                right: 46,
                bottom: 7,
            },
        };
    }
    if (iconPosition === "left") {
        icon = {
            ...icon,
            right: undefined,
            left: 9,
        };
        input = {
            ...input,
            padding: {
                ...input.padding,
                left: 36,
                right: 14,
            },
        };
        remove = {
            ...remove,
            right: 9,
        };
    }
    const useStyles = createUseStyles({
        root: {
            position: "relative",
            backgroundColor: gray.white.color,
            border: {
                width: 1,
                color: gray.smoke.color,
                style: "solid",
            },
        },
        label: { position: "absolute", left: -99999 },
        input,
        icon,
        remove,
    });
    const classes = useStyles();
    return classes;
};
